/**
 * Auth store (Zustand).
 * Manages JWT tokens, current user, and hotel context.
 * Persisted to localStorage for page refresh survival.
 *
 * _hasHydrated tracks whether persist has finished reading from localStorage.
 * RequireAuth must wait for this before deciding to redirect, otherwise the
 * default isAuthenticated=false would cause an immediate redirect on reload.
 */

import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { User } from "@/types/user";

interface AuthState {
  _hasHydrated: boolean;
  accessToken: string | null;
  refreshToken: string | null;
  user: User | null;
  isAuthenticated: boolean;

  setHasHydrated: (v: boolean) => void;
  setTokens: (accessToken: string, refreshToken: string) => void;
  setUser: (user: User) => void;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      _hasHydrated: false,
      accessToken: null,
      refreshToken: null,
      user: null,
      isAuthenticated: false,

      setHasHydrated: (v) => set({ _hasHydrated: v }),

      setTokens: (accessToken, refreshToken) =>
        set({ accessToken, refreshToken, isAuthenticated: true }),

      setUser: (user) => set({ user }),

      logout: () => {
        set({
          accessToken: null,
          refreshToken: null,
          user: null,
          isAuthenticated: false,
        });
      },
    }),
    {
      name: "rms-auth",
      // Only persist tokens and user — _hasHydrated resets on every page load
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        // Called after localStorage is read and state is merged.
        // Setting _hasHydrated=true here triggers a re-render in RequireAuth.
        state?.setHasHydrated(true);
      },
    }
  )
);
